import { useEffect, useState } from 'react';
import { EventBus, useGameStore, type ElevatorFloor } from '@campus/core';
import { Icon } from '../ds/index.js';

/**
 * ElevatorPanel — liste des étages quand l'avatar s'arrête devant un
 * ascenseur (ZoneScene.checkElevators). Un clic, ou le chiffre de l'étage au
 * clavier, y emmène ; Échap ferme la liste jusqu'à ce qu'on reparte. L'étage
 * courant est affiché sans être choisissable, avec le nombre de personnes à
 * chaque étage (présence de tout le campus, comme les bulles des portails).
 */
export function ElevatorPanel() {
  const [state, setState] = useState<{ currentSlug: string; floors: ElevatorFloor[] } | null>(null);
  const presence = useGameStore((s) => s.allZonePresence);

  useEffect(() => {
    const onOpened = (p: { currentSlug: string; floors: ElevatorFloor[] }) => setState(p);
    const onClosed = () => setState(null);
    EventBus.on('elevator:opened', onOpened);
    EventBus.on('elevator:closed', onClosed);
    return () => {
      EventBus.off('elevator:opened', onOpened);
      EventBus.off('elevator:closed', onClosed);
    };
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'Escape') {
        EventBus.emit('ui:elevator-dismiss', {});
        return;
      }
      // Chiffre de l'étage : position physique (Digit/Numpad), l'AZERTY sans Maj donne & é " '.
      const m = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
      if (!m) return;
      const floor = state.floors.find((f) => f.floor === Number(m[1]) && f.mapSlug !== state.currentSlug);
      if (floor) {
        e.preventDefault();
        EventBus.emit('ui:elevator-choose', { mapSlug: floor.mapSlug });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  if (!state) return null;
  const countOn = (slug: string) => presence.find((z) => z.zoneSlug === slug)?.players.length ?? 0;
  const floors = [...state.floors].reverse(); // le plus haut en premier, comme dans une cabine

  return (
    <div className="pl-float-bottom">
      <div className="pl-pop pl-elevator" role="dialog" aria-label="Choisir un étage">
        <div className="pl-elevator-head">
          <span>Ascenseur</span>
          <button type="button" className="pl-elevator-close" aria-label="Fermer" onClick={() => EventBus.emit('ui:elevator-dismiss', {})}>
            <Icon name="x" size={14} />
          </button>
        </div>
        {floors.map((f) => {
          const here = f.mapSlug === state.currentSlug;
          const count = countOn(f.mapSlug);
          return (
            <button
              key={f.mapSlug}
              type="button"
              className="pl-elevator-floor"
              disabled={here}
              aria-current={here ? 'location' : undefined}
              onClick={() => EventBus.emit('ui:elevator-choose', { mapSlug: f.mapSlug })}
            >
              <span className="pl-elevator-num">{f.floor}</span>
              <span className="pl-elevator-label">{f.label}</span>
              <span className="pl-elevator-meta">
                {here ? 'Tu es ici' : count > 0 ? <><Icon name="users" size={12} /> {count}</> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
